import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';

export interface AutocompleteSelectOption {
  value: string;
  label: string;
}

interface AutocompleteSelectProps {
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: string;
  value?: string;
  options: AutocompleteSelectOption[];
  onChange: (value: string, option?: AutocompleteSelectOption) => void;
  onSearch?: (query: string) => void;
  loading?: boolean;
  disabled?: boolean;
  emptyMessage?: string;
  className?: string;
  allowClear?: boolean;
  size?: 'sm' | 'md';
  footerActionLabel?: string;
  onFooterAction?: () => void;
  renderOption?: (option: AutocompleteSelectOption) => React.ReactNode;
  renderSelectedOption?: (option: AutocompleteSelectOption) => React.ReactNode;
}

const AutocompleteSelect = React.forwardRef<HTMLInputElement, AutocompleteSelectProps>(
  ({
    label,
    placeholder,
    helperText,
    error,
    value,
    options,
    onChange,
    onSearch,
    loading = false,
    disabled = false,
    emptyMessage = 'No hay opciones disponibles',
    className = '',
    allowClear = true,
    size = 'md',
    footerActionLabel,
    onFooterAction,
    renderOption,
    renderSelectedOption,
  }, ref) => {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const blurTimeout = useRef<number | null>(null);
    const sizeClass = size === 'sm' ? 'px-3 py-1.5 text-base' : 'px-3 py-2';
    const shouldFilterLocally = !onSearch;

    const selectedOption = options.find((option) => option.value === value);
    const visibleOptions = useMemo(() => {
      if (!shouldFilterLocally) {
        return options;
      }

      const query = inputValue.trim().toLowerCase();
      if (!query) {
        return options;
      }

      return options.filter((option) => option.label.toLowerCase().includes(query));
    }, [inputValue, options, shouldFilterLocally]);

    useEffect(() => {
      if (selectedOption) {
        setInputValue(selectedOption.label);
      } else if (!value) {
        setInputValue('');
      }
    }, [selectedOption, value]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      setInputValue(nextValue);
      onSearch?.(nextValue);
      setOpen(true);
    };

    const handleSelect = (option: AutocompleteSelectOption) => {
      onChange(option.value, option);
      setInputValue(option.label);
      setOpen(false);
    };

    const handleClear = () => {
      if (disabled) {
        return;
      }
      setInputValue('');
      onChange('');
      onSearch?.('');
      setOpen(false);
    };

    const handleBlur = () => {
      if (blurTimeout.current) {
        window.clearTimeout(blurTimeout.current);
      }
      blurTimeout.current = window.setTimeout(() => {
        setOpen(false);
        if (selectedOption && inputValue !== selectedOption.label) {
          setInputValue(selectedOption.label);
        }
      }, 150);
    };

    const handleFocus = () => {
      if (blurTimeout.current) {
        window.clearTimeout(blurTimeout.current);
      }
      setOpen(true);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Backspace' && selectedOption && inputValue === selectedOption.label) {
        event.preventDefault();
        setInputValue('');
        onChange('');
        onSearch?.('');
        setOpen(true);
      }
    };

    return (
      <div className="space-y-1">
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <div className="relative">
          <div className="relative">
            {selectedOption && !open && renderSelectedOption ? (
              <>
                <input
                  ref={ref}
                  type="hidden"
                  value={value ?? ''}
                  readOnly
                />
                <button
                  type="button"
                  onClick={handleFocus}
                  onBlur={handleBlur}
                  disabled={disabled}
                  className={`
                    flex w-full items-center justify-between rounded-md border border-gray-300 bg-white shadow-sm
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                    disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                    ${sizeClass}
                    ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
                    ${className}
                  `}
                >
                  <span className="flex min-h-[1.5rem] flex-1 items-center gap-2 truncate text-gray-900">
                    {renderSelectedOption(selectedOption)}
                  </span>
                </button>
              </>
            ) : (
              <input
                ref={ref}
                type="text"
                value={inputValue}
                placeholder={placeholder}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onClick={handleFocus}
                onBlur={handleBlur}
                disabled={disabled}
                className={`
                  w-full border border-gray-300 rounded-md shadow-sm
                  focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                  disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
                  ${sizeClass}
                  ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
                  ${className}
                `}
              />
            )}
          </div>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
            {allowClear && value && !disabled && (
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Clear selection"
              >
                ×
              </button>
            )}
            {loading ? (
              <Loader2 size={16} className="animate-spin text-gray-400" />
            ) : (
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleFocus}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Open options"
              >
                <ChevronDown size={16} />
              </button>
            )}
          </div>

          {open && !disabled && (
            <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
              {loading ? (
                <div className="px-3 py-2 text-sm text-gray-500">Cargando...</div>
              ) : visibleOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">{emptyMessage}</div>
              ) : (
                visibleOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(option)}
                    className="w-full cursor-pointer px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                  >
                    {renderOption ? renderOption(option) : option.label}
                  </button>
                ))
              )}

              {footerActionLabel && onFooterAction && (
                <div className="border-t border-gray-200 p-2">
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      setOpen(false);
                      onFooterAction();
                    }}
                    className="w-full rounded-md bg-gray-50 px-3 py-2 text-left text-sm font-medium text-indigo-600 hover:bg-indigo-50"
                  >
                    {footerActionLabel}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

AutocompleteSelect.displayName = 'AutocompleteSelect';

export default AutocompleteSelect;
